import { useState, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, Calendar, Upload, FileText, Image, Film } from 'lucide-react'
import { pedApi, socialApi, getApiErrorMessage } from '@/services/api'
import { cn } from '@/lib/utils'
import { PLATFORM_COLORS } from './ContentCard'
import type { ContentType, Platform, ContentStatus, SocialFormat } from '@/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ContentFormData {
  title: string
  type: ContentType
  platform: Platform
  status: ContentStatus
  scheduledAt: string
  caption: string
  hashtags: string // comma-separated
  notes: string
}

interface ContentFormModalProps {
  /** Accetta sia planId che projectId — il backend gestisce entrambi */
  planId: string
  onClose: () => void
  onSuccess?: () => void
  initialData?: Partial<ContentFormData>
}

// Use Record<string, string> to avoid exactOptionalPropertyTypes issues
type FormErrors = Record<string, string>

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_OPTIONS: { value: ContentType; label: string }[] = [
  { value: 'POST', label: 'Post' },
  { value: 'REEL', label: 'Reel' },
  { value: 'STORY', label: 'Story' },
  { value: 'CAROUSEL', label: 'Carosello' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'ARTICLE', label: 'Articolo' },
  { value: 'INFOGRAPHIC', label: 'Infografica' },
]

const PLATFORM_OPTIONS: { value: Platform; label: string }[] = [
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'TIKTOK', label: 'TikTok' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'TWITTER', label: 'Twitter / X' },
  { value: 'PINTEREST', label: 'Pinterest' },
]

const STATUS_OPTIONS: { value: ContentStatus; label: string }[] = [
  { value: 'IDEA', label: 'Idea' },
  { value: 'TODO', label: 'Da fare' },
  { value: 'IN_PRODUCTION', label: 'In Produzione' },
  { value: 'READY', label: 'Pronto' },
  { value: 'IN_REVIEW', label: 'In Review' },
  { value: 'CHANGES_REQUESTED', label: 'Modifiche Richieste' },
  { value: 'APPROVED', label: 'Approvato' },
  { value: 'SCHEDULED', label: 'Programmato' },
]

const INITIAL_DATA: ContentFormData = {
  title: '',
  type: 'POST',
  platform: 'INSTAGRAM',
  status: 'IDEA',
  scheduledAt: '',
  caption: '',
  hashtags: '',
  notes: '',
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function FormField({
  label,
  required,
  children,
  error,
  hint,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  error?: string | undefined
  hint?: string | undefined
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-danger-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
      {error && <p className="text-xs text-danger-600 mt-1">{error}</p>}
    </div>
  )
}

const inputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)

const inputErrorClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-danger-300 rounded-lg',
  'focus:outline-none focus:border-danger-500 focus:ring-1 focus:ring-danger-500/20',
  'placeholder:text-slate-400 transition-colors'
)

// Helper to clear a key from errors without setting it to undefined
function clearError(errors: FormErrors, key: string): FormErrors {
  const next = { ...errors }
  delete next[key]
  return next
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function getInitialFormData(initialData?: Partial<ContentFormData>): ContentFormData {
  const savedType = localStorage.getItem('ped-last-type') as ContentType | null

  return {
    ...INITIAL_DATA,
    // Apply remembered type as default (lowest priority)
    ...(savedType ? { type: savedType } : {}),
    // Apply initialData overrides (highest priority)
    ...initialData,
  }
}

function getInitialPlatforms(initialData?: Partial<ContentFormData>): Platform[] {
  // If initialData specifies a platform (e.g. from calendar click), use only that
  if (initialData?.platform) return [initialData.platform]

  // Otherwise restore from localStorage
  try {
    const saved = localStorage.getItem('ped-last-platforms')
    if (saved) {
      const parsed = JSON.parse(saved) as Platform[]
      if (Array.isArray(parsed) && parsed.length > 0) return parsed
    }
  } catch { /* ignore */ }

  return ['INSTAGRAM']
}

function formatBannerDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
  } catch {
    return dateStr
  }
}

export function ContentFormModal({ planId, onClose, onSuccess, initialData }: ContentFormModalProps) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<ContentFormData>(() => getInitialFormData(initialData))
  const [platforms, setPlatforms] = useState<Platform[]>(() => getInitialPlatforms(initialData))
  const [errors, setErrors] = useState<FormErrors>({})
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [successMsg, setSuccessMsg] = useState('')
  const [selectedFormatId, setSelectedFormatId] = useState<string>('')

  // Fetch formats
  const { data: formatsData } = useQuery({
    queryKey: ['social-formats', planId],
    queryFn: async () => { const res = await socialApi.listFormats(planId); return res.data },
    staleTime: 60_000,
  })
  const formats = ((formatsData as any)?.data ?? formatsData ?? []) as SocialFormat[]

  // When format is selected, update type and platforms
  const handleFormatChange = (formatId: string) => {
    setSelectedFormatId(formatId)
    if (errors.format) setErrors((prev) => clearError(prev, 'format'))
    const fmt = formats.find((f) => f.id === formatId)
    if (fmt) {
      handleChange('type', fmt.type as ContentType)
      handleChange('title', fmt.name)
      if (fmt.platforms.length > 0) {
        setPlatforms(fmt.platforms as Platform[])
        if (errors.platform) setErrors((prev) => clearError(prev, 'platform'))
      }
    }
  }

  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const results = await Promise.all(
        platforms.map((p) => pedApi.createContentForProject(planId, { ...payload, platform: p }))
      )
      return results
    },
    onSuccess: async (results) => {
      // Remember last type and platforms for next time
      localStorage.setItem('ped-last-type', formData.type)
      localStorage.setItem('ped-last-platforms', JSON.stringify(platforms))

      // Upload pending files to each created content
      if (pendingFiles.length > 0) {
        for (const res of results) {
          const contentId = (res as any)?.data?.id ?? (res as any)?.data?.data?.id
          if (contentId) {
            for (const file of pendingFiles) {
              try {
                await pedApi.uploadAsset(contentId, file)
              } catch { /* ignore upload errors */ }
            }
          }
        }
      }

      void queryClient.invalidateQueries({ queryKey: ['ped-contents', planId] })

      if (platforms.length === 1) {
        setSuccessMsg('Contenuto creato con successo!')
      } else {
        const names = platforms.map((p) => PLATFORM_OPTIONS.find((o) => o.value === p)?.label ?? p)
        setSuccessMsg(`${platforms.length} contenuti creati su ${names.join(', ')}`)
      }
      setTimeout(() => {
        onSuccess?.()
        onClose()
      }, 700)
    },
    onError: (err) => {
      setErrors((prev) => ({ ...prev, global: getApiErrorMessage(err) }))
    },
  })

  const isPending = createMutation.isPending

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, isPending])

  function validate(): boolean {
    const newErrors: FormErrors = {}
    if (!selectedFormatId && formats.length > 0) {
      newErrors.format = 'Seleziona un format'
    }
    if (!formData.title.trim()) {
      newErrors.title = "Il titolo e' obbligatorio"
    }
    if (platforms.length === 0) {
      newErrors.platform = 'Seleziona almeno una piattaforma'
    }
    if (!formData.scheduledAt) {
      newErrors.scheduledAt = 'La data di programmazione è obbligatoria'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleChange<K extends keyof ContentFormData>(field: K, value: ContentFormData[K]) {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => clearError(prev, field))
    if (errors.global) setErrors((prev) => clearError(prev, 'global'))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    const hashtagList = formData.hashtags
      .split(',')
      .map((h) => h.trim().replace(/^#/, ''))
      .filter(Boolean)

    const payload: Record<string, unknown> = {
      title: formData.title.trim(),
      type: formData.type,
      scheduledAt: formData.scheduledAt ? new Date(formData.scheduledAt).toISOString() : undefined,
      caption: formData.caption.trim() || undefined,
      hashtags: hashtagList.length > 0 ? hashtagList.join(', ') : undefined,
      body: formData.notes.trim() || undefined,
      socialFormatId: selectedFormatId || undefined,
    }

    createMutation.mutate(payload)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => { if (!isPending) onClose() }}
      />

      {/* Modal panel */}
      <div className="relative bg-white rounded-3xl shadow-modal border border-slate-200 w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Nuovo Contenuto</h2>
            <p className="text-xs text-slate-500 mt-0.5">Aggiungi un nuovo contenuto al piano editoriale</p>
          </div>
          <button
            type="button"
            onClick={() => { if (!isPending) onClose() }}
            disabled={isPending}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5">

            {/* Global error */}
            {errors.global && (
              <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <span className="text-xs text-danger-700">{errors.global}</span>
              </div>
            )}

            {/* Success */}
            {successMsg && (
              <div className="p-3 bg-success-50 border border-success-200 rounded-lg">
                <span className="text-xs text-success-700">{successMsg}</span>
              </div>
            )}

            {/* Contextual banner for pre-filled date */}
            {initialData?.scheduledAt && (
              <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary-600 flex-shrink-0" />
                <span className="text-xs font-medium text-primary-700 tabular-nums">
                  Contenuto per il {formatBannerDate(initialData.scheduledAt)}
                </span>
              </div>
            )}

            {/* Format */}
            {formats.length > 0 && (
              <FormField label="Format" required error={errors.format}>
                <select
                  value={selectedFormatId}
                  onChange={(e) => handleFormatChange(e.target.value)}
                  className={errors.format ? inputErrorClass : inputClass}
                  disabled={isPending}
                  autoFocus
                >
                  <option value="">Seleziona un format...</option>
                  {formats.filter((f) => f.isActive).map((fmt) => (
                    <option key={fmt.id} value={fmt.id}>
                      {fmt.name} — {TYPE_OPTIONS.find((t) => t.value === fmt.type)?.label ?? fmt.type}
                    </option>
                  ))}
                </select>
              </FormField>
            )}

            {/* Title (hidden if format sets it, but still editable) */}
            <FormField label="Titolo" required error={errors.title}>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="es. Post lancio collezione primavera"
                className={errors.title ? inputErrorClass : inputClass}
                disabled={isPending}
                autoFocus={formats.length === 0}
              />
            </FormField>

            {/* Piattaforme multi-select chips */}
            <FormField label="Piattaforme" required error={errors.platform} hint="Seleziona una o più piattaforme">
              <div className="flex flex-wrap gap-2">
                {PLATFORM_OPTIONS.map((opt) => {
                  const isActive = platforms.includes(opt.value)
                  const color = PLATFORM_COLORS[opt.value] ?? '#64748B'
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={isPending}
                      onClick={() => {
                        setPlatforms((prev) =>
                          prev.includes(opt.value)
                            ? prev.filter((p) => p !== opt.value)
                            : [...prev, opt.value]
                        )
                        if (errors.platform) setErrors((prev) => clearError(prev, 'platform'))
                      }}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        isActive
                          ? 'text-white border-transparent'
                          : 'text-slate-600 border-slate-200 bg-white hover:bg-slate-50'
                      )}
                      style={isActive ? { backgroundColor: color } : undefined}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </FormField>

            {/* Scheduled at */}
            <div className="grid grid-cols-1 gap-4">
              <FormField label="Data di programmazione" required error={errors.scheduledAt}>
                <input
                  type="datetime-local"
                  value={formData.scheduledAt}
                  onChange={(e) => handleChange('scheduledAt', e.target.value)}
                  className={cn(errors.scheduledAt ? inputErrorClass : inputClass, 'tabular-nums')}
                  disabled={isPending}
                />
              </FormField>
            </div>

            {/* Caption */}
            <FormField label="Caption / Copy" hint="Testo del post o corpo del contenuto">
              <textarea
                value={formData.caption}
                onChange={(e) => handleChange('caption', e.target.value)}
                placeholder="Inserisci il testo del contenuto..."
                rows={4}
                className={cn(inputClass, 'resize-none')}
                disabled={isPending}
              />
            </FormField>

            {/* Hashtags */}
            <FormField label="Hashtag" hint="Separati da virgola, es: moda, primavera2026, fashion">
              <input
                type="text"
                value={formData.hashtags}
                onChange={(e) => handleChange('hashtags', e.target.value)}
                placeholder="moda, primavera2026, fashion"
                className={inputClass}
                disabled={isPending}
              />
            </FormField>

            {/* File upload */}
            <FormField label="Media" hint="Trascina o clicca per aggiungere foto/video">
              <FileDropZone files={pendingFiles} onChange={setPendingFiles} disabled={isPending} />
            </FormField>

            {/* Notes */}
            <FormField label="Note interne" hint="Note per il team, non pubblicate">
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Note per il team di produzione..."
                rows={2}
                className={cn(inputClass, 'resize-none')}
                disabled={isPending}
              />
            </FormField>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
            <p className="text-xs text-slate-400 hidden sm:block">
              I campi con <span className="text-danger-600">*</span> sono obbligatori
            </p>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => { if (!isPending) onClose() }}
                disabled={isPending}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={isPending}
                className={cn(
                  'flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg',
                  'hover:bg-primary-700 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isPending
                  ? 'Creazione...'
                  : platforms.length > 1
                    ? `Crea ${platforms.length} Contenuti`
                    : 'Crea Contenuto'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── File Drop Zone ──────────────────────────────────────────
function FileDropZone({ files, onChange, disabled }: { files: File[]; onChange: (files: File[]) => void; disabled?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFiles = (newFiles: FileList | File[]) => {
    const arr = Array.from(newFiles)
    onChange([...files, ...arr])
  }

  const removeFile = (idx: number) => {
    onChange(files.filter((_, i) => i !== idx))
  }

  const getIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="w-3 h-3" />
    if (type.startsWith('video/')) return <Film className="w-3 h-3" />
    return <FileText className="w-3 h-3" />
  }

  return (
    <div>
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragging(false)
          if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files)
        }}
        className={cn(
          'flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
          isDragging ? 'border-primary-500 bg-primary-50' : 'border-slate-200 hover:border-primary-400 hover:bg-slate-50',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <Upload className="w-4 h-4 text-slate-400" />
        <span className="text-xs text-slate-500">
          {isDragging ? 'Rilascia qui' : 'Trascina foto/video qui o clicca per selezionare'}
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = '' }}
      />
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {files.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
              {getIcon(f.type)}
              <span className="truncate max-w-[120px]">{f.name}</span>
              <span className="text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span>
              <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-danger-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
